import { useState } from 'react';
import { generatePath, useNavigate } from 'react-router';
import { paths } from '../../common/constants/pathVariables';
import { createDraftCatalog, createVirtualCatalog, publishDraftCatalog, updateDraftCatalog } from '../../common/fetch';
import { useSearchParams } from '../../common/hooks/useSearchParams';
import { deviceListSavedAsDraftMsg, savingTheDataFailedMsg, t } from '../../common/i18n';
import { getPrimaryMdmId } from '../../common/utils/accountUtils';
import { CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE } from '../../common/utils/billingAccountUtils';
import { useAuth } from '../../public/site/AuthProvider';
import { addNotification } from '../../public/site/notificationStore.js';
import { createBillingAccountForCatalog } from '../CatalogConfiguration/utils';
import type { Catalog } from '../../generated/api/catalog';
import type { ReadyFormValues } from '../CatalogConfiguration/CatalogConfigurationForm';

export const useSubmitCatalog = () => {
  const navigate = useNavigate();
  const { authenticatedUser } = useAuth();
  const { mdmId } = useSearchParams<{ mdmId: string }>();
  const [loading, setLoading] = useState<'publish' | 'draft' | null>(null);

  const createBa = async (params: ReadyFormValues & { catalog: Catalog }) => {
    if (params.catalog.billingAccountId === CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE) {
      const resp = await createBillingAccountForCatalog(params, mdmId || getPrimaryMdmId(authenticatedUser));
      return resp.billingAccountId!;
    } else {
      return params.catalog.billingAccountId!;
    }
  };

  const upsertCatalog = async (params: ReadyFormValues & { catalog: Catalog }, cat: Catalog) => {
    // 1. If virtual catalog does not exist - create one, catalog is set as draft
    // 2. If draft exists - update it
    // 3. if draft does not exist - create it
    if (!params.virtualCatalog) {
      return createVirtualCatalog(cat, mdmId!);
    } else if (params.virtualCatalog.draft) {
      return updateDraftCatalog(params.virtualCatalog.virtualCatalogCode, cat, mdmId);
    } else {
      return createDraftCatalog(params.virtualCatalog.virtualCatalogCode, cat, mdmId);
    }
  };

  const publishCatalog = async (virtualCatalogCode: string, cat: Catalog) => {
    try {
      await publishDraftCatalog(virtualCatalogCode, cat, mdmId!);
      addNotification(t.BTQA('Catalogue published'));
      navigate(paths.COMPANY_INFO_CATALOGS);
    } catch (err) {
      addNotification(t.YY7Z('Failed to publish the catalogue'), 'error');
    }
  };

  const submitCatalog = async (params: ReadyFormValues & { catalog: Catalog }, isPublish: boolean) => {
    try {
      setLoading(isPublish ? 'publish' : 'draft');
      const billingAccountId = await createBa(params); // If the original catalog is missing a BA we allow it to continue to be absent.
      const cat = { ...params.catalog, billingAccountId };
      const resp = await upsertCatalog(params, cat);

      if (isPublish) {
        await publishCatalog(resp.virtualCatalogCode!, cat);
      } else {
        addNotification(t.P7JV(deviceListSavedAsDraftMsg));
        // Special case when an existing catalog is updated and saved as draft, stay on the catalog details page.
        const path = params.virtualCatalog?.virtualCatalogCode
          ? `${generatePath(paths.COMPANY_INFO_CATALOG, { catalogCode: params.virtualCatalog.virtualCatalogCode })}${mdmId ? '?mdmId=' + mdmId : ''}`
          : paths.COMPANY_INFO_CATALOGS;
        navigate(path);
      }
    } catch (err) {
      addNotification(t.RQA1(savingTheDataFailedMsg), 'error');
    } finally {
      setLoading(null);
    }
  };

  return { loading, submitCatalog };
};
